import React, {useEffect} from "react";
import styles from "./App.module.css";
import {createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useNavigate,} from "react-router-dom";
import {SignedIn, SignedOut, useAuth} from "@clerk/clerk-react";

import {FormProvider} from "./components/elements/formProvider/FormProvider";
import {PopupModalProvider} from "./components/elements/popupModalProvider/PopupModalProvider";

import {Login} from "./components/modules/login/Login";
import {WidgetDemo} from "./components/modules/widgetDemo/WidgetDemo";
import {RedirectToLocation} from "./components/modules/platform/redirect/RedirectToLocation";
import {SideMenu} from "./components/modules/platform/sideMenu/SideMenu";
import {Header} from "./components/modules/platform/header/Header";
import {AssistantsTab} from "./components/modules/assistant/table/wrapper/AssistantsTab";
import {LocationsTab} from "./components/modules/location/table/wrapper/LocationsTab";
import {LocationPage} from "./components/modules/location/page/LocationPage";
import {AssistantPage} from "./components/modules/assistant/page/AssistantPage";
import {Loader} from "./components/elements/loader/Loader";
import {TranscriptsTab} from "./components/modules/transcripts/table/wrapper/TranscriptsTab";
import {TranscriptPage} from "./components/modules/transcripts/page/TranscriptPage";
import {ErrorPage} from "./components/modules/error/ErrorPage";
import {ToastNotification} from "./components/elements/toastNotification/ToastNotification";
import {AnalyticsTab} from "./components/modules/analytics/page/AnalyticsTab";
import {FeedbackPage} from "./components/modules/feedback/page/FeedbackPage";
import {FranchisesTab} from "./components/modules/franchise/table/FranchisesTab";
import {FranchisePage} from "./components/modules/franchise/page/FranchisePage";
import {PublicTranscriptPage} from "./components/modules/transcripts/public/PublicTranscriptPage";
import {KnowledgeSettingsPage} from "./components/modules/knowledgeSettings/page/KnowledgeSettingsPage";
import {BulkUpdatePage} from "./components/modules/bulkUpdate/page/BulkUpdatePage";
import {BulkUpdateDetailsPage} from "./components/modules/bulkUpdate/page/BulkUpdateDetailsPage";

const SignedInLayout: React.FC = () => (
    <div className={styles.wrapper}>
        <SideMenu/>
        <div className={styles.rightSectionWrapper}>
            <Header/>
            <Outlet/>
        </div>
    </div>
);

const AuthWrapper = () => {
    const {isSignedIn, isLoaded} = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (isLoaded) {
            const isLoginPage = location.pathname === "/sign-in";

            if (!isSignedIn && !isLoginPage) {
                navigate("/sign-in", {replace: true});
            } else if (isSignedIn && isLoginPage) {
                navigate("/", {replace: true});
            }
        }
    }, [isSignedIn, isLoaded, location.pathname, navigate]);

    if (!isLoaded) return <Loader/>

    return (
        <FormProvider>
            <PopupModalProvider>
                <ToastNotification/>
                {isSignedIn ? (
                    location.pathname === "/widget-demo" ? (
                        <Outlet/>
                    ) : (
                        <SignedInLayout/>
                    )
                ) : (
                    <Outlet/>
                )}
            </PopupModalProvider>
        </FormProvider>
    );
};

const router = createBrowserRouter([
    {
        path: "/shared-transcripts/:encodedLinkData",
        element: <PublicTranscriptPage/>,
    },
    {
        element: <AuthWrapper/>,
        children: [
            {
                path: "/",
                element: <SignedIn><Outlet/></SignedIn>,
                errorElement: <ErrorPage/>,
                children: [
                    {index: true, element: <RedirectToLocation/>},
                    {path: "widget-demo", element: <WidgetDemo/>},
                    {
                        path: "locations",
                        children: [
                            {index: true, element: <LocationsTab/>},
                            {path: ":locationId", element: <LocationPage/>},
                        ],
                    },
                    {
                        path: "assistants",
                        children: [
                            {index: true, element: <AssistantsTab/>},
                            {path: ":assistantId", element: <AssistantPage/>},
                        ],
                    },
                    {
                        path: "franchises",
                        children: [
                            {index: true, element: <FranchisesTab/>},
                            {path: ":franchiseId", element: <FranchisePage/>},
                            {path: ":assistantId/:franchiseId", element: <FranchisePage/>},
                        ],
                    },
                    {
                        path: "transcripts",
                        children: [
                            {index: true, element: <TranscriptsTab/>},
                            {path: ":publicBotId/:userId", element: <TranscriptPage/>},
                        ],
                    },
                    {
                        path: "analytics",
                        children: [
                            {index: true, element: <AnalyticsTab/>},
                        ],
                    },
                    {path: "feedbacks", element: <FeedbackPage/>},
                    {path: "knowledge-settings", element: <KnowledgeSettingsPage/>},
                    {
                        path: "bulk-update",
                        children: [
                            {index: true, element: <BulkUpdatePage/>},
                            {path: ":bulkUpdateKey", element: <BulkUpdateDetailsPage/>},
                        ],
                    },
                ],
            },
            {
                element: <SignedOut><Outlet/></SignedOut>,
                children: [
                    {path: "/sign-in", element: <Login/>},
                ],
            },
            {path: "*", element: <Navigate to="/" replace/>},
        ],
    },
]);

export const App: React.FC = () => {
    return <RouterProvider router={router}/>;
};
